import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  Legend,
} from "recharts";
import {
  Loader2,
  AlertTriangle,
  TrendingUp,
  DollarSign,
  Clock,
  Star,
  Mail,
  Phone,
  CheckCircle2,
  ArrowUpDown,
} from "lucide-react";
import { cn } from "@/lib/utils";

const fmtMoney = (n: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);

const fmtMoneyFull = (n: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

// ── Aging Report ─────────────────────────────────────────────────────────────

type AgingBucket = {
  label: string;
  minDays: number;
  maxDays: number | null;
  total: number;
  accounts: {
    accountName: string;
    amount: number;
    daysOverdue: number;
    invoiceId: string;
    invoiceNumber: string;
    billingEmail: string | null;
  }[];
};

type AgingResponse = {
  totalOverdue: number;
  totalCount: number;
  buckets: AgingBucket[];
};

const BUCKET_COLORS: Record<string, string> = {
  "90+ days": "#ef4444",
  "61-90 days": "#f97316",
  "31-60 days": "#eab308",
  "1-30 days": "#3b82f6",
};

function AgingReport() {
  const { data, isLoading } = useQuery<AgingResponse>({
    queryKey: ["/api/billing/intelligence/aging"],
  });

  if (isLoading)
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );

  if (!data || data.totalCount === 0)
    return (
      <Card data-testid="aging-report-empty">
        <CardContent className="pt-6 flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
          <CheckCircle2 className="h-5 w-5" />
          <span className="font-medium">All invoices are current — no overdue AR.</span>
        </CardContent>
      </Card>
    );

  const chartData = [...data.buckets]
    .filter((b) => b.accounts.length > 0)
    .reverse()
    .map((b) => ({ label: b.label, total: b.total, count: b.accounts.length }));

  return (
    <div className="space-y-4" data-testid="aging-report">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {data.buckets.map((b) => (
          <Card
            key={b.label}
            className={cn("border", b.accounts.length === 0 && "opacity-50")}
            data-testid={`aging-bucket-${b.label.replace(/\s/g, "-")}`}
          >
            <CardHeader className="pb-1 pt-4 px-4">
              <CardTitle className="text-xs uppercase tracking-wide text-muted-foreground">
                {b.label}
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div
                className="text-2xl font-bold tabular-nums"
                style={{ color: BUCKET_COLORS[b.label] ?? "inherit" }}
              >
                {fmtMoney(b.total)}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {b.accounts.length} invoice{b.accounts.length !== 1 ? "s" : ""}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {chartData.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Overdue by Bucket</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={chartData} layout="vertical" margin={{ left: 80, right: 20, top: 4, bottom: 4 }}>
                <XAxis
                  type="number"
                  tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                  tick={{ fontSize: 11 }}
                />
                <YAxis type="category" dataKey="label" tick={{ fontSize: 12 }} width={80} />
                <Tooltip
                  formatter={(v: number) => fmtMoneyFull(v)}
                  labelFormatter={(l) => `Bucket: ${l}`}
                />
                <Bar dataKey="total" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry) => (
                    <Cell key={entry.label} fill={BUCKET_COLORS[entry.label] ?? "#6b7280"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">
            All Overdue Accounts ({data.totalCount})
          </CardTitle>
          <CardDescription>
            Total AR overdue: <span className="font-semibold text-red-600 dark:text-red-400">{fmtMoneyFull(data.totalOverdue)}</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Account</TableHead>
                <TableHead>Invoice #</TableHead>
                <TableHead>Days Overdue</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Billing Email</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.buckets.flatMap((b) =>
                b.accounts.map((acc) => (
                  <TableRow key={acc.invoiceId} data-testid={`aging-row-${acc.invoiceId}`}>
                    <TableCell className="font-medium">{acc.accountName}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{acc.invoiceNumber}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className="text-xs font-medium"
                        style={{ borderColor: BUCKET_COLORS[b.label], color: BUCKET_COLORS[b.label] }}
                      >
                        {acc.daysOverdue}d
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">
                      {fmtMoneyFull(acc.amount)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {acc.billingEmail ? (
                        <a
                          href={`mailto:${acc.billingEmail}`}
                          className="hover:underline flex items-center gap-1"
                          data-testid={`aging-email-${acc.invoiceId}`}
                        >
                          <Mail className="h-3 w-3" />
                          {acc.billingEmail}
                        </a>
                      ) : (
                        <span className="text-muted-foreground/50">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Cash Flow Forecast ────────────────────────────────────────────────────────

type CashFlowForecastResponse = {
  horizon: number;
  confirmedRevenue: number;
  confirmedCount: number;
  expectedRevenue: number;
  expectedCount: number;
  outstandingDeposits: number;
  depositCount: number;
  expectedEvents: { id: string; invoiceNumber: string; accountName: string; dateOfEvent: string; amount: number; billingEmail: string | null }[];
};

function CashFlowForecast() {
  const [horizon, setHorizon] = useState<"30" | "60" | "90">("30");
  const { data, isLoading } = useQuery<CashFlowForecastResponse>({
    queryKey: ["/api/billing/intelligence/cash-flow-forecast", { horizon }],
    queryFn: () =>
      fetch(`/api/billing/intelligence/cash-flow-forecast?horizon=${horizon}`, {
        credentials: "include",
      }).then((r) => r.json()),
  });

  return (
    <div className="space-y-4" data-testid="cash-flow-forecast">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-muted-foreground">Forecast window:</span>
        <Select value={horizon} onValueChange={(v) => setHorizon(v as any)}>
          <SelectTrigger className="w-36" data-testid="select-forecast-horizon">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="30">Next 30 days</SelectItem>
            <SelectItem value="60">Next 60 days</SelectItem>
            <SelectItem value="90">Next 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center p-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Card data-testid="forecast-confirmed">
              <CardHeader className="pb-1 pt-4 px-4">
                <CardTitle className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  Confirmed Revenue
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="text-2xl font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
                  {fmtMoney(data.confirmedRevenue)}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {data.confirmedCount} paid event{data.confirmedCount !== 1 ? "s" : ""}
                </div>
              </CardContent>
            </Card>

            <Card data-testid="forecast-expected">
              <CardHeader className="pb-1 pt-4 px-4">
                <CardTitle className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-blue-500" />
                  Expected Revenue
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="text-2xl font-bold tabular-nums text-blue-600 dark:text-blue-400">
                  {fmtMoney(data.expectedRevenue)}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {data.expectedCount} booked, unpaid event{data.expectedCount !== 1 ? "s" : ""}
                </div>
              </CardContent>
            </Card>

            <Card data-testid="forecast-deposits">
              <CardHeader className="pb-1 pt-4 px-4">
                <CardTitle className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                  Outstanding Deposits
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className={cn("text-2xl font-bold tabular-nums", data.outstandingDeposits > 0 ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground")}>
                  {fmtMoney(data.outstandingDeposits)}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {data.depositCount > 0
                    ? `${data.depositCount} deposit${data.depositCount !== 1 ? "s" : ""} not yet collected`
                    : "All deposits collected"}
                </div>
              </CardContent>
            </Card>
          </div>

          {data.expectedEvents.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">
                  Booked-but-Unpaid Events ({data.expectedCount})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Account</TableHead>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Event Date</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Billing Email</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.expectedEvents.map((ev) => (
                      <TableRow key={ev.id} data-testid={`forecast-event-${ev.id}`}>
                        <TableCell className="font-medium">{ev.accountName}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{ev.invoiceNumber}</TableCell>
                        <TableCell className="text-sm">
                          {ev.dateOfEvent
                            ? (() => {
                                // dateOfEvent is a calendar-day date column (YYYY-MM-DD) —
                                // parse without tz-shift to avoid UTC-midnight day drift.
                                const [y, mo, d] = (ev.dateOfEvent as string).split("-").map(Number);
                                return new Date(y, mo - 1, d).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                });
                              })()
                            : "—"}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-semibold">
                          {fmtMoneyFull(ev.amount)}
                        </TableCell>
                        <TableCell className="text-sm">
                          {ev.billingEmail ? (
                            <a
                              href={`mailto:${ev.billingEmail}`}
                              className="hover:underline flex items-center gap-1 text-muted-foreground"
                              data-testid={`forecast-email-${ev.id}`}
                            >
                              <Mail className="h-3 w-3" />
                              {ev.billingEmail}
                            </a>
                          ) : (
                            <span className="text-muted-foreground/50">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ── Collection Priority ───────────────────────────────────────────────────────

type CollectionAccount = {
  rank: number;
  accountName: string;
  amount: number;
  daysOverdue: number;
  phone: string | null;
  email: string | null;
  fastPayer: boolean;
  score: number;
  invoiceIds: string[];
};

type CollectionPriorityResponse = {
  accounts: CollectionAccount[];
};

type CollSortField = "rank" | "amount" | "daysOverdue";

function CollectionPriority() {
  const [sortField, setSortField] = useState<CollSortField>("rank");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const { data, isLoading } = useQuery<CollectionPriorityResponse>({
    queryKey: ["/api/billing/intelligence/collection-priority"],
  });

  const toggleSort = (field: CollSortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sorted = [...(data?.accounts ?? [])].sort((a, b) => {
    const mul = sortDir === "asc" ? 1 : -1;
    if (sortField === "amount") return (a.amount - b.amount) * mul;
    if (sortField === "daysOverdue") return (a.daysOverdue - b.daysOverdue) * mul;
    return (a.rank - b.rank) * mul;
  });

  if (isLoading)
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );

  if (!data || data.accounts.length === 0)
    return (
      <Card data-testid="collection-priority-empty">
        <CardContent className="pt-6 flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
          <CheckCircle2 className="h-5 w-5" />
          <span className="font-medium">No overdue invoices — nothing to collect on right now.</span>
        </CardContent>
      </Card>
    );

  const SortButton = ({ field, label }: { field: CollSortField; label: string }) => (
    <button
      onClick={() => toggleSort(field)}
      className="flex items-center gap-1 hover:text-foreground transition-colors"
      data-testid={`sort-collection-${field}`}
    >
      {label}
      <ArrowUpDown className="h-3 w-3" />
    </button>
  );

  return (
    <div className="space-y-4" data-testid="collection-priority">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">
            Collection Priority ({data.accounts.length} account{data.accounts.length !== 1 ? "s" : ""})
          </CardTitle>
          <CardDescription>
            Ranked by overdue amount × days past due. Historically fast-paying clients are demoted 30%.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <SortButton field="rank" label="#" />
                </TableHead>
                <TableHead>Account</TableHead>
                <TableHead>
                  <SortButton field="daysOverdue" label="Days Overdue" />
                </TableHead>
                <TableHead className="text-right">
                  <SortButton field="amount" label="Amount" />
                </TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Note</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((acc) => (
                <TableRow
                  key={`${acc.accountName}-${acc.rank}`}
                  data-testid={`collection-row-${acc.rank}`}
                >
                  <TableCell className="font-bold text-muted-foreground">{acc.rank}</TableCell>
                  <TableCell className="font-medium">{acc.accountName}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={cn(
                        "text-xs",
                        acc.daysOverdue >= 90
                          ? "border-red-500 text-red-500"
                          : acc.daysOverdue >= 60
                          ? "border-orange-500 text-orange-500"
                          : acc.daysOverdue >= 30
                          ? "border-yellow-500 text-yellow-600"
                          : "border-blue-500 text-blue-500"
                      )}
                    >
                      {acc.daysOverdue}d
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">
                    {fmtMoneyFull(acc.amount)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {acc.email && (
                        <a
                          href={`mailto:${acc.email}`}
                          title={acc.email}
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          data-testid={`collection-email-${acc.rank}`}
                        >
                          <Mail className="h-4 w-4" />
                        </a>
                      )}
                      {acc.phone && (
                        <a
                          href={`tel:${acc.phone}`}
                          title={acc.phone}
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          data-testid={`collection-phone-${acc.rank}`}
                        >
                          <Phone className="h-4 w-4" />
                        </a>
                      )}
                      {!acc.email && !acc.phone && (
                        <span className="text-muted-foreground/50 text-xs">No contact</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {acc.fastPayer && (
                      <Badge variant="secondary" className="text-xs">
                        Fast payer
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Revenue Trend ─────────────────────────────────────────────────────────────

type RevenueBucket = {
  year: number;
  month: number;
  label: string;
  paidRevenue: number;
  bookedRevenue: number;
};

type RevenueTrendResponse = {
  basis: string;
  numMonths: number;
  buckets: RevenueBucket[];
};

function RevenueTrend() {
  const [months, setMonths] = useState<"3" | "6" | "12">("6");
  const [basis, setBasis] = useState<"event_date" | "paid_date">("event_date");

  const { data, isLoading } = useQuery<RevenueTrendResponse>({
    queryKey: ["/api/billing/intelligence/revenue-trend", { months, basis }],
    queryFn: () =>
      fetch(`/api/billing/intelligence/revenue-trend?months=${months}&basis=${basis}`, {
        credentials: "include",
      }).then((r) => r.json()),
  });

  const peak =
    data?.buckets.reduce(
      (best, b) => (b.paidRevenue > best.paidRevenue ? b : best),
      data.buckets[0]
    ) ?? null;

  return (
    <div className="space-y-4" data-testid="revenue-trend">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={months} onValueChange={(v) => setMonths(v as any)}>
          <SelectTrigger className="w-36" data-testid="select-trend-months">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="3">Last 3 months</SelectItem>
            <SelectItem value="6">Last 6 months</SelectItem>
            <SelectItem value="12">Last 12 months</SelectItem>
          </SelectContent>
        </Select>
        <Select value={basis} onValueChange={(v) => setBasis(v as any)}>
          <SelectTrigger className="w-44" data-testid="select-trend-basis">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="event_date">By event date (accrual)</SelectItem>
            <SelectItem value="paid_date">By paid date (cash)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center p-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : !data ? null : (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Revenue Trend — {data.numMonths} months
            </CardTitle>
            {peak && peak.paidRevenue > 0 && (
              <CardDescription>
                Peak month:{" "}
                <span className="font-semibold text-foreground">{peak.label}</span> at{" "}
                <span className="font-semibold text-foreground">{fmtMoney(peak.paidRevenue)}</span>
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.buckets} margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis
                  tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                  tick={{ fontSize: 11 }}
                  width={52}
                />
                <Tooltip
                  formatter={(v: number, name: string) => [
                    fmtMoneyFull(v),
                    name === "paidRevenue" ? "Paid Revenue" : "Booked Revenue",
                  ]}
                  labelFormatter={(l) => `Month: ${l}`}
                />
                <Legend
                  formatter={(v) =>
                    v === "paidRevenue" ? "Paid Revenue" : "Booked Revenue"
                  }
                />
                <Line
                  type="monotone"
                  dataKey="paidRevenue"
                  stroke="#2563eb"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                  data-testid="line-paid-revenue"
                />
                <Line
                  type="monotone"
                  dataKey="bookedRevenue"
                  stroke="#7c3aed"
                  strokeWidth={2}
                  strokeDasharray="4 2"
                  dot={{ r: 3 }}
                  data-testid="line-booked-revenue"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Client Performance (Top Clients) ─────────────────────────────────────────

type TopClient = {
  rank: number;
  name: string;
  paidRevenue: number;
  eventCount: number;
  avgDealSize: number;
};

type TopClientsResponse = {
  clients: TopClient[];
};

type TopClientSortField = "rank" | "paidRevenue" | "eventCount" | "avgDealSize";

function ClientPerformance() {
  const [sortField, setSortField] = useState<TopClientSortField>("rank");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const { data, isLoading } = useQuery<TopClientsResponse>({
    queryKey: ["/api/billing/intelligence/top-clients"],
  });

  const toggleSort = (field: TopClientSortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sorted = [...(data?.clients ?? [])].sort((a, b) => {
    const mul = sortDir === "asc" ? 1 : -1;
    if (sortField === "paidRevenue") return (a.paidRevenue - b.paidRevenue) * mul;
    if (sortField === "eventCount") return (a.eventCount - b.eventCount) * mul;
    if (sortField === "avgDealSize") return (a.avgDealSize - b.avgDealSize) * mul;
    return (a.rank - b.rank) * mul;
  });

  if (isLoading)
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );

  if (!data || data.clients.length === 0)
    return (
      <Card data-testid="top-clients-empty">
        <CardContent className="pt-6 text-muted-foreground text-sm">
          No paid invoices on record yet — no client rankings to show.
        </CardContent>
      </Card>
    );

  const SortButton = ({ field, label }: { field: TopClientSortField; label: string }) => (
    <button
      onClick={() => toggleSort(field)}
      className="flex items-center gap-1 hover:text-foreground transition-colors"
      data-testid={`sort-clients-${field}`}
    >
      {label}
      <ArrowUpDown className="h-3 w-3" />
    </button>
  );

  const maxRevenue = Math.max(...(data.clients.map((c) => c.paidRevenue)), 1);

  return (
    <div className="space-y-4" data-testid="client-performance">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Star className="h-4 w-4 text-yellow-500" />
            Client Performance — Top {data.clients.length}
          </CardTitle>
          <CardDescription>Ranked by total lifetime paid revenue.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <SortButton field="rank" label="#" />
                </TableHead>
                <TableHead>Client</TableHead>
                <TableHead className="text-right">
                  <SortButton field="paidRevenue" label="Paid Revenue" />
                </TableHead>
                <TableHead className="text-right">
                  <SortButton field="eventCount" label="Events" />
                </TableHead>
                <TableHead className="text-right">
                  <SortButton field="avgDealSize" label="Avg Deal" />
                </TableHead>
                <TableHead className="hidden md:table-cell">Revenue Share</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((client) => {
                const pct = (client.paidRevenue / maxRevenue) * 100;
                return (
                  <TableRow key={client.rank} data-testid={`client-row-${client.rank}`}>
                    <TableCell>
                      <span className={cn("font-bold text-sm", client.rank === 1 && "text-yellow-500")}>
                        {client.rank === 1 ? "🥇" : client.rank === 2 ? "🥈" : client.rank === 3 ? "🥉" : `#${client.rank}`}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium">{client.name}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">
                      {fmtMoneyFull(client.paidRevenue)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {client.eventCount}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {fmtMoney(client.avgDealSize)}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full transition-all"
                            style={{ width: `${pct.toFixed(1)}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground w-10 text-right">
                          {pct.toFixed(0)}%
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Main Tab ──────────────────────────────────────────────────────────────────

type IntelSection = "aging" | "cash-flow" | "collections" | "trend" | "clients";

const SECTIONS: { id: IntelSection; label: string; icon: typeof DollarSign }[] = [
  { id: "aging", label: "Aging Report", icon: AlertTriangle },
  { id: "cash-flow", label: "Cash Flow Forecast", icon: Clock },
  { id: "collections", label: "Collection Priority", icon: Phone },
  { id: "trend", label: "Revenue Trend", icon: TrendingUp },
  { id: "clients", label: "Client Performance", icon: Star },
];

export function BillingIntelligenceTab() {
  const [activeSection, setActiveSection] = useState<IntelSection>("aging");

  return (
    <div className="space-y-5" data-testid="billing-intelligence-tab">
      <div>
        <h2 className="text-base font-semibold mb-1">Billing Intelligence</h2>
        <p className="text-sm text-muted-foreground">
          Financial analytics — the same data your voice assistant can answer, now visible on screen.
        </p>
      </div>

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Intelligence sections">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          const isActive = activeSection === s.id;
          return (
            <Button
              key={s.id}
              variant={isActive ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveSection(s.id)}
              className="flex items-center gap-1.5"
              data-testid={`intel-section-${s.id}`}
              role="tab"
              aria-selected={isActive}
            >
              <Icon className="h-3.5 w-3.5" />
              {s.label}
            </Button>
          );
        })}
      </div>

      <div>
        {activeSection === "aging" && <AgingReport />}
        {activeSection === "cash-flow" && <CashFlowForecast />}
        {activeSection === "collections" && <CollectionPriority />}
        {activeSection === "trend" && <RevenueTrend />}
        {activeSection === "clients" && <ClientPerformance />}
      </div>
    </div>
  );
}
